import { z } from 'zod';
import { INTEGRATION_EVENTS, type IntegrationEventType } from './integration-events';
import { formatNotificationMoney, type DigestSectionView } from './notification-digests';
import { formatDate } from './utils/date-format';
import { getAdminUrl } from './utils/subdomain-urls';

// A message described as data rather than as Block Kit. Everything here is plain
// JSON so the same shape can later be stored per subscription and edited in the
// admin UI; renderers turn it into whatever their provider speaks.
export interface TemplateField {
	label: string;
	value: string;
}

export interface MessageTemplate {
	// The provider's own notification/preview line.
	text: string;
	headline: string;
	// 'header' renders larger and carries no markdown; 'section' allows bold.
	headlineStyle: 'header' | 'section';
	fields: TemplateField[];
	context?: string;
}

export interface ResolvedMessage {
	text: string;
	headline: string;
	headlineStyle: 'header' | 'section';
	fields: TemplateField[];
	context?: string;
	sections?: DigestSectionView[];
}

const TOKEN = /\{(\w+)\}/g;

// A value a template asked for but the payload does not carry. Rendering the raw
// token would ship "{eventName}" to a channel, which is worse than an obvious gap.
const MISSING = '—';

export function interpolate(text: string, variables: Record<string, string>): string {
	return text.replace(TOKEN, (_match, name: string) => variables[name] ?? MISSING);
}

function formatWhen(payload: Record<string, unknown>): string {
	const timezone = (payload.timezone as string | null) ?? undefined;
	const startDate = payload.startDate as string | undefined;
	if (!startDate) return MISSING;

	const suffix = timezone ? ` (${timezone})` : '';
	const start = formatDate({ date: startDate, ...(timezone ? { formatTimezone: timezone } : {}) });
	const endDate = payload.endDate as string | null | undefined;
	if (!endDate) return `${start}${suffix}`;

	const end = formatDate({ date: endDate, ...(timezone ? { formatTimezone: timezone } : {}) });
	return `${start} – ${end}${suffix}`;
}

function formatWhere(payload: Record<string, unknown>): string {
	const venueName = payload.venueName as string | null | undefined;
	if (!venueName) return 'Virtual';
	const venueAddress = payload.venueAddress as string | null | undefined;
	return [venueName, venueAddress].filter(Boolean).join('\n');
}

// Values a template can use that are not payload fields: formatted dates, a venue
// with its virtual fallback, and booleans turned into the words people read.
// Derivation lives beside the templates so a future editor can offer the same
// variable list the renderer will actually resolve.
// Resolved at render time rather than stored in the payload: a link baked into an
// outbox row would outlive the domain it points at.
// Templates are static strings, so a count and its noun have to be derived
// together or every message reads "1 attendees".
function pluralize(value: unknown, noun: string): string {
	const count = Number(value);
	if (!Number.isFinite(count)) return MISSING;
	return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

// The key is always present, even with nothing to point at, because
// templateVariablesFor probes with an empty payload to build the editor's
// variable list. A conditional key would simply vanish from it.
function eventLink(payload: Record<string, unknown>): Record<string, string> {
	const eventId = payload.eventId as string | undefined;
	return { eventLink: eventId ? getAdminUrl(`/events/${eventId}`, true) : MISSING };
}

// A group buyer's registration covers several tickets; a record from before amounts were carried has neither.
function registrationDetails(payload: Record<string, unknown>): Record<string, string> {
	const eventId = payload.eventId as string | undefined;
	const [attendeeId] = (payload.attendeeIds as string[] | undefined) ?? [];
	const quantity = payload.ticketQuantity as number | null | undefined;
	const ticketName = payload.ticketName as string | null | undefined;
	const paid = payload.amountPaid as { amountCents: number; currency: string } | null | undefined;
	return {
		registrationLink: eventId
			? getAdminUrl(attendeeId ? `/events/${eventId}/attendees/${attendeeId}` : `/events/${eventId}/attendees`, true)
			: MISSING,
		ticketLine: ticketName ? (quantity ? `${ticketName} × ${quantity}` : ticketName) : MISSING,
		amountPaid: paid
			? paid.amountCents === 0
				? 'Free'
				: formatNotificationMoney(paid.amountCents / 100, paid.currency)
			: MISSING
	};
}

function digestScopeName(payload: Record<string, unknown>): string {
	const scope = payload.scope as { kind?: string; eventName?: string; count?: number } | undefined;
	if (scope?.kind === 'event' && scope.eventName) return scope.eventName;
	if (scope?.kind === 'events' && scope.count) return `${scope.count} events`;
	// Sits mid-sentence in the email and as the Slack headline, where the
	// organization's name reads naturally and "All events" does not.
	return (payload.organizationName as string | undefined) || 'All events';
}

// Every section counts its rows the same way, so the summary reads the total
// rather than any one stat the planner may have switched off.
function digestSummary(payload: Record<string, unknown>): string {
	const sections = (payload.sections as Array<{ totalRows?: number }> | undefined) ?? [];
	const total = sections.reduce((sum, s) => sum + (s.totalRows ?? 0), 0);
	return `${total} new registration${total === 1 ? '' : 's'}`;
}

const WINDOW_LABELS: Record<string, string> = {
	hourly: 'in the last hour',
	daily: 'in the last day',
	weekly: 'in the last week'
};

function digestWindowLabel(payload: Record<string, unknown>): string {
	const cadence = (payload.window as { cadence?: string } | undefined)?.cadence;
	return (cadence && WINDOW_LABELS[cadence]) || MISSING;
}

function derivedVariables(
	type: IntegrationEventType,
	payload: Record<string, unknown>
): Record<string, string> {
	switch (type) {
		case 'event.created':
			return {
				when: formatWhen(payload),
				where: formatWhere(payload),
				createdBy: (payload.createdByName as string) || (payload.createdByEmail as string) || MISSING,
				...eventLink(payload)
			};
		case 'event.ended':
			return {
				...eventLink(payload),
				attendeeSummary: pluralize(payload.attendeeCount, 'attendee')
			};
		case 'event.goal_reached':
			return eventLink(payload);
		case 'user.first_login':
			return { who: (payload.name as string) || (payload.email as string) || MISSING };
		case 'payments.onboarding_started':
			return { accountMode: payload.testMode ? 'Test mode' : 'Live' };
		case 'payments.onboarding_completed':
			return {
				charges: payload.chargesEnabled ? 'Enabled' : 'Disabled',
				payouts: payload.payoutsEnabled ? 'Enabled' : 'Disabled'
			};
		case 'registration.first_completed':
		case 'registration.completed':
			return {
				...eventLink(payload),
				...registrationDetails(payload),
				// "Someone registered" reads as a sentence where an em dash reads as
				// a bug, so this one falls back to a word rather than to MISSING.
				who: (payload.attendeeName as string) || (payload.attendeeEmail as string) || 'Someone'
			};
		case 'notification.digest':
			return {
				scopeName: digestScopeName(payload),
				summary: digestSummary(payload),
				windowLabel: digestWindowLabel(payload)
			};
		default:
			return {};
	}
}

export function templateVariablesFor(type: IntegrationEventType): string[] {
	const schema = INTEGRATION_EVENTS[type]?.schema;
	const shape = schema instanceof z.ZodObject ? Object.keys(schema.shape) : [];
	return [...shape, ...Object.keys(derivedVariables(type, {}))].sort();
}

export function variablesFor(
	type: IntegrationEventType,
	payload: Record<string, unknown>
): Record<string, string> {
	const scalars: Record<string, string> = {};
	for (const [key, value] of Object.entries(payload ?? {})) {
		if (value === null || value === undefined || typeof value === 'object') continue;
		scalars[key] = String(value);
	}
	return { ...scalars, ...derivedVariables(type, payload ?? {}) };
}

export const DEFAULT_MESSAGE_TEMPLATES: Record<IntegrationEventType, MessageTemplate> = {
	'event.created': {
		text: 'New event created: {eventName} ({organizationName})',
		headline: '{eventName}',
		headlineStyle: 'header',
		fields: [
			{ label: 'Organization', value: '{organizationName}' },
			{ label: 'When', value: '{when}' },
			{ label: 'Where', value: '{where}' },
			{ label: 'Created by', value: '{createdBy}' }
		],
		context: '{createdByEmail} · <{eventLink}|Open in Sunfish>'
	},
	'event.ended': {
		text: 'Event ended: {eventName}',
		headline:
			'*Event ended*: {eventName} ({organizationName}) · {attendeeSummary} · <{eventLink}|Open in Sunfish>',
		headlineStyle: 'section',
		fields: []
	},
	'event.goal_reached': {
		text: 'Goal reached: {eventName}',
		headline:
			'*Goal reached*: {eventName} ({organizationName}) · {metric} {actual} of {target} · <{eventLink}|Open in Sunfish>',
		headlineStyle: 'section',
		fields: []
	},
	'user.first_login': {
		text: 'First login: {who}',
		headline: '*First login*: {who} ({email}) at {organizationName}',
		headlineStyle: 'section',
		fields: []
	},
	'payments.onboarding_started': {
		text: 'Stripe onboarding started: {organizationName}',
		headline: '*Stripe onboarding started*: {organizationName} · {accountMode} · {startedByEmail}',
		headlineStyle: 'section',
		fields: []
	},
	'payments.onboarding_completed': {
		text: 'Stripe onboarding completed: {organizationName}',
		headline:
			'*Stripe onboarding complete*: {organizationName} · charges {charges}, payouts {payouts}',
		headlineStyle: 'section',
		fields: []
	},
	'registration.completed': {
		text: 'New registration: {eventName}',
		headline: '{who} registered for {eventName}',
		headlineStyle: 'section',
		fields: [
			{ label: 'Ticket', value: '{ticketName}' },
			{ label: 'Email', value: '{attendeeEmail}' }
		],
		context: '<{eventLink}|Open in Sunfish>'
	},
	'registration.first_completed': {
		text: 'First registration for {eventName}',
		headline: '{eventName} has its first attendee',
		headlineStyle: 'header',
		fields: [
			{ label: 'Attendee', value: '{who}' },
			{ label: 'Email', value: '{attendeeEmail}' },
			{ label: 'Ticket', value: '{ticketLine}' },
			{ label: 'Amount paid', value: '{amountPaid}' }
		],
		context: '<{registrationLink}|View registration>'
	},
	'notification.digest': {
		text: '{summary} for {scopeName}',
		headline: '*{scopeName}*: {summary} {windowLabel}',
		headlineStyle: 'section',
		fields: [],
		context: '{organizationName}'
	}
};

// Slack caps a section at 10 fields, and a template that exceeds it is rejected
// at delivery time rather than at save time, which is far too late to tell anyone.
export const MAX_TEMPLATE_FIELDS = 10;

export const messageTemplateSchema = z.object({
	text: z.string().trim().min(1),
	headline: z.string().trim().min(1),
	headlineStyle: z.enum(['header', 'section']),
	fields: z
		.array(
			z.object({
				label: z.string().trim().min(1),
				value: z.string().trim().min(1)
			})
		)
		.max(MAX_TEMPLATE_FIELDS),
	context: z.string().trim().optional()
});

export function tokensIn(template: MessageTemplate): string[] {
	const text = [
		template.text,
		template.headline,
		template.context ?? '',
		...template.fields.flatMap((field) => [field.label, field.value])
	].join(' ');

	return [...new Set(Array.from(text.matchAll(TOKEN), (match) => match[1]))];
}

// Saving a template that references a variable the event cannot supply would ship
// an em dash to a channel forever. Cheaper to refuse it while someone is looking.
export function unknownTokensIn(type: IntegrationEventType, template: MessageTemplate): string[] {
	const available = new Set(templateVariablesFor(type));
	return tokensIn(template).filter((token) => !available.has(token));
}

// The seam for per-subscription templates. Today every caller passes nothing and
// gets the default; when templates become editable, the stored one arrives here
// and no renderer or call site changes.
export function resolveMessageTemplate(
	type: IntegrationEventType,
	override?: Partial<MessageTemplate> | null
): MessageTemplate | undefined {
	const base = DEFAULT_MESSAGE_TEMPLATES[type];
	if (!base) return undefined;
	if (!override) return base;
	return { ...base, ...override };
}

export function renderMessageTemplate(
	template: MessageTemplate,
	variables: Record<string, string>
): ResolvedMessage {
	return {
		text: interpolate(template.text, variables),
		headline: interpolate(template.headline, variables),
		headlineStyle: template.headlineStyle,
		// A field whose value resolves to nothing is dropped rather than rendered
		// as an empty row, which several providers reject outright.
		fields: template.fields
			.map((field) => ({
				label: field.label,
				value: interpolate(field.value, variables).trim()
			}))
			.filter((field) => field.value && field.value !== MISSING),
		...(template.context ? { context: interpolate(template.context, variables) } : {})
	};
}
